/* eslint-disable max-lines */

import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import type { AdminsPresencesMap } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { AttachmentsActions, AttachmentsSelectors } from '@core/attachments-data';
import { filterBoolean, PageVisibilityService } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { NoteActions } from '@core/notes-data';
import {
  type CurrentlyTypedNotes,
  type EditableNotesListState,
  injectSetRecruiteeFilteredMentions,
  NotesAnalytics,
  provideFilteredMentions,
} from '@core/notes-legacy';
import {
  currentlyTypedNotes$,
  handleDeleteNote,
  handleDeleteNoteSuccess,
  handleDiscardNote,
  handleEditNote,
  handleNoteAttachmentDeleteSuccess,
  handleNoteSaveStart,
  handleNoteSaveSuccess,
  handleReplyToNote,
  handleUploadNoteAttachment,
  handleUploadNoteAttachmentFailure,
  handleUploadNoteAttachmentSuccess,
  NoteFormComponent,
  notesChannel$,
  NotesListComponent,
  notesWebsockets$,
} from '@core/notes-legacy';
import { RecruiteeNotesService } from '@core/notes-recruitee-logic';
import type {
  Note,
  NoteDeleteAttachmentEvent,
  NoteDiscardEvent,
  NoteEditEvent,
  NoteId,
  NotePayload,
  NoteReplyClickEvent,
  NoteSave,
  NoteUploadAttachmentEvent,
} from '@core/notes-types';
import { NEW_NOTE_ID } from '@core/notes-types';
import {
  EmptyStateComponent,
  NotificationsController,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';
import type { NotesChannel } from '@core/websocket-legacy';
import { AdminSocket } from '@core/websocket-legacy';

import { NotesPageDataService } from '../../../data/single-requisition/notes-page-data.service';
import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';

@Component({
  selector: 'rt-requisitions-notes-container',
  templateUrl: './requisitions-notes-container.component.html',
  styleUrls: ['./requisitions-notes-container.component.less'],
  imports: [
    EmptyStateComponent,
    SpinnerComponent,
    NoteFormComponent,
    NotesListComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideFilteredMentions(), NotesAnalytics],
})
export class RequisitionsNotesContainerComponent implements OnInit {
  private readonly data = inject(NotesPageDataService);
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly actions$ = inject(Actions);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly adminSocket = inject(AdminSocket);
  private readonly pageVisibilityService = inject(PageVisibilityService);
  private readonly requisitionsGeneralFacade = inject(RequisitionsGeneralFacade);

  private readonly filteredMentionsSet = injectSetRecruiteeFilteredMentions();
  private readonly analytics = inject(NotesAnalytics);
  public readonly recruiteeNotesService = inject(RecruiteeNotesService);

  public readonly NEW_NOTE_ID = NEW_NOTE_ID;

  public currentUser: CurrentUser;
  public hasHighLevelDataAccess = false;
  public isLoaded = false;
  public isReadOnlyMode = false;
  public localStorageKeyBuilder = this.recruiteeNotesService.localStorageKeyBuilderFactory({});

  public state: EditableNotesListState = {
    notes: {
      data: [],
    },
    noteAttachmentsBeingUploaded: {},
    noteAttachmentsTemp: {},
    notesBeingSaved: [],
    notesBeingDeleted: [],
    notesBeingEdited: [],
    notesBeingEditedCache: [],
    notesBeingRepliedTo: [],
    notesWithError: [],
  };
  public currentlyTypedNote?: { replyToId?: NoteId; lastTypeAt: number };
  public currentlyTypedNotes: CurrentlyTypedNotes = [];

  private adminsPresencesMap$ = new Subject<AdminsPresencesMap>();
  private requisitionId: number;

  private ngOnDestroy$ = new Subject<void>();
  private channel: NotesChannel;

  public ngOnInit() {
    combineLatest([
      this.data.notes$,
      this.data.users$,
      this.data.currentUser$,
      this.data.abilities$,
      this.data.requisition$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([notes, users, currentUser, abilities, requisition]) => {
        this.state = {
          ...this.state,
          notes: {
            data: notes,
          },
        };
        this.filteredMentionsSet(users, []);
        this.currentUser = currentUser;
        this.hasHighLevelDataAccess = abilities[Ability.HighLevelDataAccess];
        this.requisitionId = requisition.id;
        this.isReadOnlyMode = this.requisitionsGeneralFacade.isReadOnlyMode;
        this.cdRef.detectChanges();
      });

    this.data.isLoaded$.pipe(filterBoolean, takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.isLoaded = true;
      this.cdRef.detectChanges();
    });

    this.handleStoreActions();
    this.handleWebsockets();
  }

  public noteSaveClick(payload: NoteSave<Note>): void {
    const { note, attachmentIds } = payload;
    this.state = handleNoteSaveStart(this.state, { note });
    this.cdRef.detectChanges();

    const action = Number.isInteger(note.id)
      ? NoteActions.updateOnePessimistic({
          payload: { data: { ...note, id: <number>note.id }, attachmentIds },
        })
      : NoteActions.createRequisitionNote({
          payload: {
            note: {
              ...note,
              id: null,
            },
            requisitionId: this.requisitionId,
            attachmentIds,
            correlationId: generateUuid(),
          },
        });
    this.store.dispatch(action);
    this.analytics.trackNoteAdding('requisition');
  }

  public noteDiscardClick(note: NoteDiscardEvent) {
    this.state = handleDiscardNote(this.state, note);
    this.cdRef.detectChanges();
  }

  public noteEditClick(note: NoteEditEvent) {
    if (!this.state.notesBeingEdited.includes(note.id!)) {
      this.state = handleEditNote(this.state, note);
      this.cdRef.detectChanges();
    }
  }

  public uploadAttachmentClick(payload: NoteUploadAttachmentEvent): void {
    const uuid = generateUuid();

    this.state = handleUploadNoteAttachment(this.state, {
      note: payload.note,
      uuid,
    });
    this.cdRef.detectChanges();

    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: { file: payload.attachment && payload.attachment.file, correlationId: uuid },
      }),
    );

    this.handleAttachmentSuccess(uuid, payload);
    this.handleAttachmentFailed(uuid, payload);
  }

  public deleteAttachmentClick(payload: NoteDeleteAttachmentEvent<Note>): void {
    this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: payload.attachmentId }));

    this.actions$
      .pipe(
        ofType(AttachmentsActions.deleteAttachmentSuccess),
        take(1),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.state = handleNoteAttachmentDeleteSuccess(this.state, payload);
        this.cdRef.detectChanges();
      });
  }

  public noteChecklistClick({ id, bodyJson }: { id: number; bodyJson: object }) {
    this.store.dispatch(NoteActions.updateChecklist({ payload: { id, bodyJson } }));
  }

  public noteDeleteClick(noteId: NoteId) {
    this.state = handleDeleteNote(this.state, { noteId });
    this.cdRef.detectChanges();
    this.store.dispatch(NoteActions.deleteRequisitionNote({ payload: { data: noteId } }));
  }

  public notePinClick(noteId: number): void {
    this.store.dispatch(NoteActions.pin({ payload: { id: noteId } }));
  }

  public noteUnpinClick(noteId: number): void {
    this.store.dispatch(NoteActions.unpin({ payload: { id: noteId } }));
  }

  public noteReplyClick(note: NoteReplyClickEvent): void {
    if (!this.state.notesBeingRepliedTo.includes(note.id!)) {
      this.state = handleReplyToNote(this.state, note);
      this.cdRef.detectChanges();
    }
  }

  public handleNoteTypingStart(note: { replyToId?: NoteId }): void {
    if (
      this.currentlyTypedNote &&
      this.currentlyTypedNote.replyToId === note.replyToId &&
      new Date().getTime() - this.currentlyTypedNote.lastTypeAt < 1000
    ) {
      return;
    }

    if (this.channel) {
      this.currentlyTypedNote = { replyToId: note.replyToId, lastTypeAt: new Date().getTime() };
      this.cdRef.detectChanges();
      this.channel.push('started-typing-note', { reply_to_id: note.replyToId || null });
    }
  }

  public handleNoteTypingFinish(): void {
    if (this.channel && this.currentlyTypedNote) {
      this.channel.push('finished-typing-note', {});
      this.currentlyTypedNote = undefined;
      this.cdRef.detectChanges();
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private handleStoreActions() {
    this.actions$
      .pipe(ofType(NoteActions.createRequisitionNoteSuccess))
      .subscribe(({ payload }) => {
        this.state = handleNoteSaveSuccess(this.state, {
          note: {
            ...(<NotePayload>payload.data),
            id: <NotePayload['id']>(<unknown>NEW_NOTE_ID),
          },
        });
        this.cdRef.detectChanges();
      });

    this.actions$.pipe(ofType(NoteActions.updateOnePessimisticSuccess)).subscribe(({ payload }) => {
      this.state = handleNoteSaveSuccess(this.state, { note: <NotePayload>payload.data });
      this.cdRef.detectChanges();
    });

    this.actions$
      .pipe(ofType(NoteActions.deleteRequisitionNoteSuccess))
      .subscribe(({ payload }) => {
        this.state = handleDeleteNoteSuccess(this.state, { id: <number>payload.data.id });
        this.cdRef.detectChanges();
      });
  }

  private handleAttachmentSuccess(uuid: string, payload: NoteUploadAttachmentEvent) {
    this.store
      .select(AttachmentsSelectors.byId(uuid))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(attachment => {
        this.state = handleUploadNoteAttachmentSuccess(this.state, {
          ...payload,
          uuid,
          attachment,
        });
        this.cdRef.detectChanges();
      });
  }

  private handleAttachmentFailed(uuid: string, payload: NoteUploadAttachmentEvent) {
    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
        take(1),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(error => {
        if (uuid === error.payload.correlationId) {
          this.notificationsCtrl.error(error.payload.error.error.error[0]);

          this.state = handleUploadNoteAttachmentFailure(this.state, {
            ...payload,
            uuid: error.payload.correlationId,
          });
          this.cdRef.detectChanges();
        }
      });
  }

  private handleWebsockets() {
    const pageVisibleTrigger = () =>
      this.pageVisibilityService.isPageVisible()
        ? of(null)
        : this.pageVisibilityService.$onPageVisible;

    const channel$ = notesChannel$(
      this.adminSocket,
      `requisition-notes:${this.requisitionId}`,
    ).pipe(tap(channel => (this.channel = channel)));

    notesWebsockets$(
      channel$,
      pageVisibleTrigger,
      presences => this.adminsPresencesMap$.next(presences),
      () =>
        this.store.dispatch(
          NoteActions.fetchRequisitionNotes({
            payload: { requisitionId: this.requisitionId },
          }),
        ),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe();

    this.handleAdminPresencesUpdates();
  }

  private handleAdminPresencesUpdates() {
    const admins$ = this.data.users$;
    currentlyTypedNotes$(admins$, this.adminsPresencesMap$, this.currentUser.id)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentlyTypedNotes => {
        this.currentlyTypedNotes = currentlyTypedNotes;
        this.cdRef.detectChanges();
      });
  }
}
